import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Hammer,
  Paintbrush,
  Brush,
  LayoutTemplate,
  Layers,
  DoorOpen,
  Wrench,
  Building2,
  Home,
  ArrowRight,
  ArrowUpRight,
  Check,
} from "lucide-react";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import CompleteDelivery from "@/components/site/CompleteDelivery";
import ScopeBanner from "@/components/site/ScopeBanner";
import QuoteForm from "@/components/site/QuoteForm";
import RecentProjects from "@/components/site/RecentProjects";

const SERVICES = [
  {
    key: "demolition-strip-out",
    icon: Hammer,
    title: "Demolition & Strip-Out",
    tag: "Stage 01",
    desc: "Soft strip, partition, floor, ceiling, kitchen and bathroom removal. Licensed waste carriers. The essential first stage of every refurbishment.",
    bullets: ["Soft strip demolition", "Partition removal", "Floor & ceiling strip-out", "Commercial strip-outs"],
    href: "/demolition-strip-out-london",
  },
  {
    key: "painting-decorating",
    icon: Paintbrush,
    title: "Painting & Decorating",
    desc: "Professional prep, spray and hand-finished decorating for homes and offices. Premium paints, mirror-flat walls, sharp lines.",
    bullets: ["Spray & brush finishes", "Feature walls", "Wallpaper hanging", "External decorating"],
  },
  {
    key: "plastering",
    icon: Brush,
    title: "Plastering",
    desc: "Skim, float-and-set, re-plastering and patch repairs. Polished plaster available for high-end projects.",
    bullets: ["Skim coats", "Float & set", "Patch repairs", "Polished plaster"],
  },
  {
    key: "drywall-partitions",
    icon: LayoutTemplate,
    title: "Drywall & Partitions",
    desc: "Metal-stud, timber, acoustic and fire-rated partition systems — built to spec for offices, flats and commercial fit-outs.",
    bullets: ["Metal-stud systems", "Acoustic partitions", "Timber framing", "Fire-rated walls"],
  },
  {
    key: "flooring-tiling",
    icon: Layers,
    title: "Flooring & Tiling",
    desc: "Engineered wood, LVT, laminate, vinyl, porcelain, ceramic and natural stone — preparation through installation.",
    bullets: ["Engineered wood & LVT", "Screed & levelling", "Porcelain & ceramic", "Wet-room tanking"],
  },
  {
    key: "carpentry-doors",
    icon: DoorOpen,
    title: "Carpentry & Doors",
    desc: "1st and 2nd fix carpentry, bespoke joinery, door hanging, skirting, architrave and built-in storage.",
    bullets: ["1st & 2nd fix", "Bespoke joinery", "Door hanging", "Skirting & architrave"],
  },
  {
    key: "repairs-maintenance",
    icon: Wrench,
    title: "Repairs & Maintenance",
    desc: "Small jobs welcome — urgent repairs, snagging, ongoing maintenance contracts and property upkeep.",
    bullets: ["Urgent repairs", "Snagging & patching", "Maintenance plans", "Landlord handover fixes"],
  },
  {
    key: "commercial-refurbishment",
    icon: Building2,
    title: "Commercial Refurbishment",
    desc: "Offices, retail and hospitality — Cat-A / Cat-B fit-outs, dilapidations and out-of-hours programmes for landlords, tenants and PMs.",
    bullets: ["Cat-A / Cat-B fit-out", "Retail & F&B", "Dilapidations", "Out-of-hours works"],
    large: true,
  },
  {
    key: "residential-refurbishment",
    icon: Home,
    title: "Residential Refurbishment",
    desc: "Full flat and house refurbishments, kitchens, bathrooms, loft conversions and rental turnarounds. From a single room to whole-home programmes.",
    bullets: ["Full refurbishments", "Kitchens & bathrooms", "HMO & rental turnarounds", "Landlord packages"],
    large: true,
  },
];

export default function ServicesPage() {
  return (
    <main data-testid="services-page" className="bg-white text-[#2B2420]">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="services-hero"
        className="bg-white pt-32 md:pt-40 pb-16 md:pb-20 border-b border-[#E5DDD0]"
      >
        <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5">
              <span className="brand-gold-divider" /> Services
            </div>
            <h1
              className="font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] text-[#2B2420]"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Nine disciplines. <br /> One contractor.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-[#4D4037] text-base md:text-lg leading-relaxed">
              Every stage from the first hammer to the final snag — delivered
              by the same team, under one programme, with one point of contact.
            </p>
            <div className="mt-6">
              <a
                href="#quote"
                data-testid="services-hero-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
              >
                Request a Free Quote <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section data-testid="services-list-section" className="py-20 md:py-28 bg-white">
        <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-4 border-t border-l border-[#E5DDD0]">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const Inner = (
              <motion.div
                data-testid={`services-page-card-${s.key}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="group relative border-r border-b border-[#E5DDD0] p-7 md:p-10 bg-white hover:bg-[#F5EFE5] transition-colors flex flex-col justify-between min-h-[320px] md:col-span-2"
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-7 h-7 text-[#B8732C] group-hover:text-[#C9A24C] transition-colors" strokeWidth={1.5} />
                  <ArrowUpRight className="w-5 h-5 text-[#8A7F74] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <div className="mt-8">
                  {s.tag && <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C] font-bold mb-2">{s.tag}</div>}
                  <h3
                    className="font-black tracking-tight text-2xl md:text-3xl leading-[1.05] text-[#2B2420]"
                    style={{ fontFamily: "Cabinet Grotesk" }}
                  >
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-[#8A7F74] leading-relaxed max-w-xl">{s.desc}</p>
                  <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-xs md:text-sm text-[#2B2420]">
                        <Check className="w-3.5 h-3.5 text-[#C9A24C] mt-0.5 shrink-0" strokeWidth={3} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
            return s.href ? (
              <Link to={s.href} key={s.key} className="md:col-span-2">
                {Inner}
              </Link>
            ) : (
              <a href="#quote" key={s.key} className="md:col-span-2">
                {Inner}
              </a>
            );
          })}
        </div>
      </section>

      <CompleteDelivery eyebrow="Complete Project Delivery" />
      <RecentProjects />
      <ScopeBanner />
      <QuoteForm />
      <Footer />
      <MobileStickyCTA />
    </main>
  );
}
